import {
  ImportContacts,
  Schedule,
  EventRepeatOutlined,
  AutoFixHigh,
  Launch,
} from "@mui/icons-material";
import {
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Divider,
  ClickAwayListener,
  Paper,
  Typography,
  Chip,
  IconButton,
  Button,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import React, { useState } from "react";
import { AuthenticationContext, } from "../../Context";
import GeneralModal from "../../modals/GeneralModal";
import { HtmlTooltip } from "../../patient_reports/health_overview/panels/action_center/ActionRow";
import { getHeaders } from "../../utils/HeaderHelper";
import ActionQueueHistoryModal from "./ActionQueueHistoryModal";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SVG from "react-inlinesvg";
import { snakeCaseToTitleCase } from "../../utils/CaseFormatHelper";
import Collapse from "@material-ui/core/Collapse";
import CustomChip from "../../shared/CustomChip";
import NewEncounter from "../../modals/NewEncounter";
import DueDateModal from "./DueDateModal";
import { useFlash } from "../../FlashContext";

const StatusElement = ({
  currentStatus,
  onStatusChange,
  currentActionId = null,
  dueDate = null,
  reloadData = null,
}) => {
  const statuses = ["incomplete", "complete", "skipped"];
  const orderedStatuses = [
    currentStatus,
    ...statuses.filter((status) => status !== currentStatus),
  ];

  const [open, setOpen] = useState(false);
  const [showDueDateModal, setShowDueDateModal] = React.useState(false);
  const collapseRef = React.useRef(null);

  // Handle menu toggle
  const handleToggle = () => {
    setOpen(!open);
  };

  React.useEffect(() => {
    if (open && collapseRef.current) {
      const scrollableContainer = collapseRef.current.closest(
        ".action-queue-layout"
      );
      const collapse = collapseRef.current.getBoundingClientRect();
      const containerRect = scrollableContainer.getBoundingClientRect();
      const collapseNotVisible = containerRect.bottom - collapse.top < 194;

      if (collapseNotVisible) {
        collapseRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [open]);

  const getChipColor = (status) => {
    switch (status) {
      case "complete":
        return "success";
      case "incomplete":
        return "error";
      case "skipped":
        return "warning";
      default:
        return "info";
    }
  };

  return (
    <Box sx={{ position: "relative", display: "inline-block" }}>
      {showDueDateModal && (
        <DueDateModal
          open={showDueDateModal}
          onCloseModal={() => {
            setShowDueDateModal(false);
          }}
          actionQueueId={currentActionId}
          dueDate={dueDate}
          reloadData={reloadData}
        />
      )}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconButton onClick={handleToggle} size="small">
          {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
        </IconButton>
        <CustomChip
          label={snakeCaseToTitleCase(currentStatus)}
          type={getChipColor(currentStatus)}
          onClick={handleToggle}
        />
      </Box>

      <Collapse
        ref={collapseRef}
        in={open}
        timeout="auto"
        unmountOnExit
        style={{
          position: "absolute",
          top: "100%",
          left: 0,
          zIndex: 10,
          width: "max-content",
        }}
      >
        <Paper elevation={3} sx={{ mt: 1, p: 2, borderRadius: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {orderedStatuses?.map((status) => (
              <CustomChip
                key={status}
                label={snakeCaseToTitleCase(status)}
                type={status === currentStatus ? "info" : getChipColor(status)}
                onClick={
                  status != currentStatus
                    ? (e) => {
                        onStatusChange(e, status);
                        handleToggle();
                      }
                    : undefined
                }
              />
            ))}
            {currentActionId && (
              <>
                <Box
                  style={{
                    height: "1px",
                    backgroundColor: "#D9D9D9",
                    width: "100%",
                    marginTop: "5px",
                  }}
                ></Box>
                <Button
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    lineHeight: "20px",
                    textAlign: "center",
                    fontFamily: "QuicksandBold",
                    textDecoration: "none",
                    textTransform: "none",
                    color: "#1E1E1E",
                    padding: "0px",
                  }}
                  variant="text"
                  onClick={() => {
                    setShowDueDateModal(true);
                    setOpen(false);
                  }}
                >
                  Change Due Date
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

const ActionItem = ({
  aq,
  providers,
  patientOnly = false,
  reloadData,
  reloadCount,
  reloadPrograms,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = React.useState(false);
  const [showHistoryFor, setShowHistoryFor] = React.useState<number>();
  const [openEncounterModal, setOpenEncounterModal] = React.useState(false);

  const handleExpandToggle = () => {
    setExpanded(!expanded);
  };
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [resourceOwner, setResourceOwner] = useState<string>();
  const [resources, setResources] = useState<any>([]);
  const [recurrenceToolTipOpen, setRecurrenceToolTipOpen] =
    React.useState(false);
  const [activeAutomationTooltipId, setActiveAutomationTooltipId] =
    React.useState(null);
  const [activeQLTooltipId, setActiveQLTooltipId] = React.useState(null);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openDropdown = Boolean(anchorEl);
  const handleDropDownClick = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleCloseDropdown = () => {
    setAnchorEl(null);
  };

  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const handleUnassignAction = async () => {
    try {
      handleCloseDropdown();
      const response = await fetch(
        `/action_queues/${aq.action_queue_id}/unassign`,
        {
          method: "PUT",
          headers: getHeaders(authenticationSetting.csrfToken),
        }
      );
      const result = await response.json();
      showToast({
        title: result.success == false ? "Error" : "Success",
        text: result.message,
        type: result.success == false ? "error" : "success",
      });
      reloadData();
    } catch (error) {
      console.error(error);
      showToast({
        title: "Error",
        text: "Something went wrong.",
        type: "error",
      });
    }
  };

  const handleAssignActionToProvider = async (
    providerId,
    assignToMe = false
  ) => {
    try {
      handleCloseDropdown()
      const response = await fetch(
        `/action_queues/${aq.action_queue_id}/assign_to_provider`,
        {
          method: "PUT",
          headers: getHeaders(authenticationSetting.csrfToken),
          body: JSON.stringify({
            provider_id: providerId,
            assign_to_me: assignToMe ? 1 : 0,
          }),
        }
      );
      const result = await response.json();
      showToast({
        title: result.success == false ? "Error" : "Success", 
        text: result.message,
        type: result.success == false ? "error" : "success",
      });
      reloadData();
    } catch (error) {
      console.error(error);
      showToast({
        title: "Error",
        text: "Something went wrong.",
        type: "error",
      });
    }
  };

  const handleShowResourceClick = (event, resources, type = "action") => {
    event.stopPropagation();
    setResourceOwner(type);
    setResources(resources);
    setShowResourceModal(true);
  };

  const handleRecurrenceTooltipClose = () => {
    setRecurrenceToolTipOpen(false);
  };

  const handleRecurrenceTooltipToggle = (event) => {
    event.stopPropagation();
    setRecurrenceToolTipOpen(!recurrenceToolTipOpen);
  };

  const handleAutomationTooltipClose = () => {
    setActiveAutomationTooltipId(null);
  };

  const handleAutomationTooltipToggle = (event, stepId) => {
    event.stopPropagation();
    setActiveQLTooltipId(null);
    setActiveAutomationTooltipId(
      activeAutomationTooltipId === stepId ? null : stepId
    );
  };

  const handleQLTooltipClose = () => {
    setActiveQLTooltipId(null);
  };

  const handleQLTooltipToggle = (event, stepId) => {
    event.stopPropagation();
    setActiveAutomationTooltipId(null);
    setActiveQLTooltipId(activeQLTooltipId === stepId ? null : stepId);
  };

  const handleLaunchQuestionnaire = (event, qId) => {
    event.stopPropagation();
    if (qId) {
      fetch(`/questionnaire_assignments`, {
        method: "POST",
        headers: getHeaders(authenticationSetting.csrfToken),
        body: JSON.stringify({
          questionnaire_assignment: {
            patient_id: aq?.patient_id,
            questionnaire_id: qId,
            assignment_type: "manual",
          },
        }),
      })
        .then((result) => result.json())
        .then((result) => {
          if (result.success == false) {
            alert(result.error);
          } else {
            if (result?.success) {
              if (result.resource?.assignment_type == "manual") {
                window.open(
                  `/questionnaire_assignments_submission_prov/${result.resource?.uuid}`
                );
              }
              showToast({
                title: "Success",
                text: result.message,
                type: "success",
              });
            } else {
              showToast({
                title: "Error",
                text: result.message,
                type: "error",
              });
            }
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      showToast({
        title: "Unable to assign questionnaire",
        text: "Cannot assign without selecting questionnaire",
        type: "error",
      });
    }
  };

  const handleToggleStatus = async (event, status: string) => {
    event.preventDefault();
    if (aq.status === status) {
      return;
    }
    const incompleteSteps = aq.action_steps.filter(
      (step) =>
        step.step_status !== "complete" && step.step_status !== "skipped"
    );
    if (
      (status === "complete" || status === "skipped") &&
      incompleteSteps.length > 0
    ) {
      showToast({
        title: "Cannot change Action status",
        text: "All steps must be marked as complete or skipped before marking the action as complete or skipped.",
        type: "error",
      });
      return;
    }

    aq.status = status;
    try {
      const response = await fetch(
        `/action_queues/${aq.action_queue_id}/update_status`,
        {
          method: "PUT",
          headers: getHeaders(authenticationSetting.csrfToken),
          body: JSON.stringify({
            status: status,
          }),
        }
      );
      const result = await response.json();
      showToast({
        title : result.success == false ? "Error" : "Success",
        text: result.message,
        type: result.success == false ? "error" : "success",
      });
      if (result.success && status === "complete") {
        reloadPrograms(true);
      }
    } catch (error) {
      console.error(error);
      showToast({
        title: "Error",
        text: "Something went wrong.",
        type: "error",
      });
      reloadData();
    } finally {
      reloadCount();
    }
  };

  const handleUpdateStepStatus = async (event, step, status: string) => {
    event.preventDefault();
    if (step.step_status === status) {
      return;
    }
    if (aq.status === "complete" || aq.status === "skipped") {
      showToast({
        title: "Unable to change Step status",
        text: "You cannot change the status of any step after the action is marked as complete or skipped.",
        type: "error",
      });
      return;
    }
    try {
      const response = await fetch(
        `/action_queues/${aq.action_queue_id}/steps/${step.id}/update_step_status`,
        {
          method: "PUT",
          headers: getHeaders(authenticationSetting.csrfToken),
          body: JSON.stringify({
            status: status,
          }),
        }
      );
      const result = await response.json();
      showToast({
        title: result.success == false ? "Error" : "Success",
        text: result.message,
        type: result.success == false ? "error" : "success",
      });
      const response_resource = result.resource;
      reloadData(response_resource);
    } catch (error) {
      console.error(error);
      showToast({
        title: "Error",
        text: "Something went wrong.",
        type: "error",
      });
      reloadCount();
      reloadData();
    }
  };

  const cellFlexBasis = patientOnly ? "25%" : "20%";

  return (
    <>
      <GeneralModal
        open={showResourceModal}
        title={`Action ${resourceOwner === "step" ? "Step " : ""} Resource(s)`}
        successCallback={undefined}
        closeCallback={() => {
          setShowResourceModal(false);
          setResources([]);
        }}
        showContinueIcon={false}
        showCancelButton={false}
      >
        <Grid container>
          <Grid item xs={12} mt={3}>
            {!!resources && resources?.length > 0 ? (
              resources.map((resource) => (
                <li key={resource.id}>
                  <a href={resource.link} target="_blank" rel="noreferrer">
                    {resource.name}
                  </a>
                </li>
              ))
            ) : (
              <div>No associated resources</div>
            )}
          </Grid>
        </Grid>
      </GeneralModal>
      {showHistoryModal && setShowHistoryFor && (
        <ActionQueueHistoryModal
          open={showHistoryModal}
          onCloseModal={() => {
            setShowHistoryFor(undefined);
            setShowHistoryModal(false);
          }}
          actionQueueId={showHistoryFor}
        />
      )}
      <Box
        display={"flex"}
        width={"100%"}
        alignItems={"center"}
        ml={2}
        sx={
          expanded
            ? {
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.6)",
                zIndex: 2,
                py: 1,
                marginTop: 2,
              }
            : {
                marginTop: 2,
              }
        }
      >
        <Box
          className="action-table-row"
          sx={{
            flexBasis: "18%",
            display: "flex",
            alignItems: "left",
            pl: "10px",
            pr: "18px",
          }}
        >
          <StatusElement
            currentStatus={aq?.status}
            onStatusChange={(e, status: string) =>
              handleToggleStatus(e, status)
            }
            currentActionId={aq.action_queue_id}
            dueDate={aq.due_date}
            reloadData={reloadData}
          />
        </Box>
        {!patientOnly && (
          <Box className="action-table-row" sx={{ flexBasis: cellFlexBasis }}>
            <div>
              <a
                href={`/patient_reports/${aq.patient_id}/action_center`}
                target="_blank"
                style={{
                  textTransform: "capitalize",
                  color: "black",
                }}
                rel="noreferrer"
              >{`${aq.patient_last_name}, ${aq.patient_first_name}`}</a>
            </div>
          </Box>
        )}
        <Box className="action-table-row" sx={{ flexBasis: cellFlexBasis }}>
          {aq.action_title}
        </Box>
        <Box className="action-table-row" sx={{ flexBasis: cellFlexBasis }}>
          {aq.action_subject}
        </Box>
        <Box className="action-table-row" sx={{ flexBasis: cellFlexBasis }}>
          <Stack
            direction={"row"}
            spacing={1}
            display={"flex"}
            alignItems={"center"}
          >
            <List
              component="nav"
              aria-label="Assign Provider"
              sx={{
                bgcolor: "background.paper",
                background: "unset !important",
              }}
            >
              <ListItemButton
                id="lock-button"
                aria-haspopup="listbox"
                aria-controls="lock-menu"
                aria-label="when device is locked"
                aria-expanded={open ? "true" : undefined}
                onClick={handleDropDownClick}
                sx={{ padding: "0 !important", fontSize: "unset" }}
              >
                <ListItemText
                  primary={
                    <Stack direction={"row"} spacing={1} fontSize={"0.875rem"}>
                      {aq.assigned_provider_id ? (
                        <>
                          <KeyboardArrowDownIcon /> &nbsp;
                          {`${aq.provider_last_name}, ${aq.provider_first_name}`}
                        </>
                      ) : (
                        <a href="#" onClick={(e) => e.preventDefault()}>
                          Assign Action
                        </a>
                      )}
                    </Stack>
                  }
                />
              </ListItemButton>
            </List>
            <Menu
              id="status-menu"
              anchorEl={anchorEl}
              open={openDropdown}
              onClose={handleCloseDropdown}
              MenuListProps={{
                "aria-labelledby": "lock-button",
                role: "listbox",
              }}
            >
              {!aq?.assigned_provider_id ? (
                <MenuItem
                  onClick={() => handleAssignActionToProvider(null, true)}
                >
                  Assign to me
                </MenuItem>
              ) : (
                <MenuItem onClick={handleUnassignAction}>
                  Unassign this Action
                </MenuItem>
              )}
              <Divider />
              {providers?.map((provider, index) => (
                <MenuItem
                  key={`${aq.id} - ${provider.id}`}
                  onClick={() => {
                    handleAssignActionToProvider(provider.id);
                  }}
                >
                  {provider.name}
                </MenuItem>
              ))}
            </Menu>
          </Stack>
        </Box>
        <Box
          className="action-table-row"
          sx={{
            flexBasis: "20%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Stack
            direction={"row"}
            spacing={3}
            alignItems={"baseline"}
            justifyContent={"space-between"}
            width={"100%"}
            pr={3}
          >
            {!!aq.action_resources && aq.action_resources.length > 0 ? (
              <ImportContacts
                style={{ fontSize: "1.5em", cursor: "pointer" }}
                onClick={(event) =>
                  handleShowResourceClick(event, aq.action_resources, "action")
                }
              />
            ) : (
              <Box width={"25px"}></Box>
            )}
            <Schedule
              style={{ fontSize: "1.5em", cursor: "pointer" }}
              onClick={() => {
                setShowHistoryFor(aq.action_queue_id);
                setShowHistoryModal(true);
              }}
            />
            <ClickAwayListener onClickAway={handleRecurrenceTooltipClose}>
              <div>
                <HtmlTooltip
                  PopperProps={{
                    disablePortal: true,
                  }}
                  onClose={handleRecurrenceTooltipClose}
                  open={recurrenceToolTipOpen}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  placement="top"
                  arrow
                  title={
                    <Paper
                      elevation={3}
                      sx={{
                        py: 2,
                        px: 2,
                        background: "#EFE9E8",
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: -1,
                        }}
                      >
                        Recurrence
                        <EventRepeatOutlined
                          fontSize="small"
                          sx={{ marginLeft: 1 }}
                        />
                      </Typography>
                      <p>
                        {aq?.readable_recurrence?.length > 0 ? (
                          <>
                            <span
                              style={{
                                textDecoration: aq.due_date_changed
                                  ? "line-through"
                                  : "none",
                              }}
                            >
                              {aq.readable_recurrence}
                            </span>
                            {aq.due_date_changed && <br />}
                            {aq.due_date_changed && (
                              <span style={{ fontWeight: "700" }}>
                                Due date changed.
                              </span>
                            )}
                          </>
                        ) : (
                          "Does not Repeat"
                        )}
                      </p>
                    </Paper>
                  }
                  sx={{
                    tooltip: {
                      backgroundColor: "white",
                      color: "black",
                    },
                    arrow: {
                      color: "white",
                    },
                  }}
                >
                  <EventRepeatOutlined
                    style={{ fontSize: "1.5em", cursor: "pointer" }}
                    onClick={handleRecurrenceTooltipToggle}
                  />
                </HtmlTooltip>
              </div>
            </ClickAwayListener>
            {aq.action_steps.length > 0 ? (
              <div onClick={handleExpandToggle} style={{ width: "25px" }}>
                {expanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              </div>
            ) : (
              <Box width={"25px"} />
            )}
          </Stack>
        </Box>
      </Box>
      {expanded &&
        aq.action_steps?.map((step, index) => (
          <Box
            key={index + "-" + step.id}
            sx={{
              display: "flex",
              alignItems: "center",
              py: 1,
              my: 2,
              ml: 2,
              width: "100%",
            }}
          >
            <Box
              className="action-table-row"
              sx={{
                flexBasis: "10%",
                display: "flex",
                alignItems: "left",
                ml: 2,
              }}
            >
              <StatusElement
                currentStatus={step?.step_status}
                onStatusChange={(e, status: string) =>
                  handleUpdateStepStatus(e, step, status)
                }
              />
            </Box>
            <Box className="action-table-row" sx={{ flexBasis: "20%" }}>
              <Box
                display={"flex"}
                justifyContent={"start"}
                alignItems={"center"}
                ml={1}
              >
                <SVG
                  src={step.icon_url}
                  width={25}
                  height={25}
                  fill={"black"}
                  aria-placeholder={"Icon"}
                  style={{
                    marginRight: 8,
                  }}
                />
                {step.title}
              </Box>
            </Box>
            <Box className="action-table-row" sx={{ flexBasis: "20%" }}></Box>
            <Box className="action-table-row" sx={{ flexBasis: "40%" }}>
              <Stack direction={"row"} gap={3}>
                <ClickAwayListener onClickAway={handleAutomationTooltipClose}>
                  <div>
                    <HtmlTooltip
                      PopperProps={{
                        disablePortal: true,
                      }}
                      onClose={handleAutomationTooltipClose}
                      open={activeAutomationTooltipId === step.id}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      placement="top"
                      arrow
                      title={
                        <Paper
                          elevation={3}
                          sx={{
                            py: 2,
                            px: 2,
                            background: "#EFE9E8",
                          }}
                        >
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: -1,
                            }}
                          >
                            Automation
                            <AutoFixHigh
                              fontSize="small"
                              sx={{ marginLeft: 1 }}
                            />
                          </Typography>
                          {step?.action_step_automations.length > 0 ? (
                            step?.action_step_automations?.map((automation) => (
                              <p key={automation.id}>
                                {automation?.automation_type ===
                                "questionnaire" ? (
                                  <span>
                                    <b style={{ textTransform: "capitalize" }}>
                                      {automation?.questionnaire?.name}
                                    </b>
                                    {` questionnaire will be sent to the patient.`}
                                  </span>
                                ) : (
                                  ""
                                )}
                              </p>
                            ))
                          ) : (
                            <p>No automations</p>
                          )}
                        </Paper>
                      }
                      sx={{
                        tooltip: {
                          backgroundColor: "white",
                          color: "black",
                        },
                        arrow: {
                          color: "white",
                        },
                      }}
                    >
                      <Stack
                        direction={"row"}
                        gap={1}
                        alignItems={"center"}
                        sx={{ cursor: "pointer" }}
                        onClick={(event) =>
                          handleAutomationTooltipToggle(event, step.id)
                        }
                      >
                        <AutoFixHigh /> Automation &nbsp;
                        <Chip
                          label={step?.action_step_automations?.length}
                          size="small"
                          sx={{ px: "3px" }}
                        />
                      </Stack>
                    </HtmlTooltip>
                  </div>
                </ClickAwayListener>
                <ClickAwayListener onClickAway={handleQLTooltipClose}>
                  <div>
                    <HtmlTooltip
                      PopperProps={{
                        disablePortal: true,
                      }}
                      onClose={handleQLTooltipClose}
                      open={activeQLTooltipId === step.id}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      placement="top"
                      arrow
                      title={
                        <Paper
                          elevation={3}
                          sx={{
                            py: 2,
                            px: 2,
                            background: "#EFE9E8",
                          }}
                        >
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: -1,
                            }}
                          >
                            Quick Launch
                            <Launch fontSize="small" sx={{ marginLeft: 1 }} />
                          </Typography>
                          {step?.action_step_quick_launches?.length > 0 ? (
                            step?.action_step_quick_launches?.map((launch) => (
                              <p key={launch.id}>
                                {launch?.launch_type === "encounter" && (
                                  <a
                                    href="javascript:;"
                                    onClick={() => {
                                      setOpenEncounterModal(true);
                                    }}
                                  >
                                    New Encounter Note
                                  </a>
                                )}
                                {launch?.launch_type === "questionnaire" && (
                                  <a
                                    href={`#`}
                                    onClick={(event) =>
                                      handleLaunchQuestionnaire(
                                        event,
                                        launch?.questionnaire?.id
                                      )
                                    }
                                  >
                                    {`Quick Launch "${launch?.questionnaire?.name}" Questionnaire`}
                                  </a>
                                )}
                              </p>
                            ))
                          ) : (
                            <p>No Quick Launch</p>
                          )}
                        </Paper>
                      }
                      sx={{
                        tooltip: {
                          backgroundColor: "white",
                          color: "black",
                        },
                        arrow: {
                          color: "white",
                        },
                      }}
                    >
                      <Stack
                        direction={"row"}
                        gap={1}
                        alignItems={"center"}
                        sx={{ cursor: "pointer" }}
                        onClick={(event) =>
                          handleQLTooltipToggle(event, step.id)
                        }
                      >
                        <Launch /> Quick Launch &nbsp;
                        <Chip
                          label={step?.action_step_quick_launches?.length}
                          size="small"
                          sx={{ px: "3px" }}
                        />
                      </Stack>
                    </HtmlTooltip>
                  </div>
                </ClickAwayListener>
              </Stack>
            </Box>
            <Box
              className="action-table-row"
              sx={{
                flexBasis: "20%",
                display: "flex",
                justifyContent: "end",
              }}
            >
              <Stack
                direction={"row"}
                spacing={3}
                alignItems={"baseline"}
                justifyContent={"space-between"}
                width={"100%"}
                pr={3}
              >
                {!!step?.action_step_resources &&
                  step?.action_step_resources.length > 0 && (
                    <ImportContacts
                      style={{ fontSize: "1.5em", cursor: "pointer" }}
                      onClick={(event) =>
                        handleShowResourceClick(
                          event,
                          step?.action_step_resources,
                          "step"
                        )
                      }
                    />
                  )}
                <Box width={"20px"}></Box>
                <Box width={"20px"}></Box>
                <Box width={"20px"}></Box>
              </Stack>
            </Box>
          </Box>
        ))}
      {openEncounterModal && (
        <NewEncounter
          modalOpen={openEncounterModal}
          setModalOpen={setOpenEncounterModal}
          preSetEncounterType={
            aq.assigned_program?.program?.program_type == "diabetes_management"
              ? "diabetes_management"
              : null
          }
          patientId={aq.patient_id}
        />
      )}
    </>
  );
};

export default ActionItem;
